import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  EMPTY,
  catchError,
  combineLatest,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import {
  Category,
  CategoryService,
  ConfigService,
  Layered,
  LayeredService,
  PageService,
  ProductionService,
} from '@speedy/core';

interface HomeCategory {
  id: string;
  title: string;
}

const HOME_CATEGORY_ID_FOR_TRANSFER_STATE = 'home_category_1';
const HOME_CATEGORY_ID_2_FOR_TRANSFER_STATE = 'home_category_2';
const HOME_PAGE_DATA_FOR_TRANSFER_STATE = 'home_page_data';

@Injectable({ providedIn: 'root' })
export class HomeResolver {
  PAGE_SIZE = 6;

  constructor(
    private categoryService: CategoryService,
    private layeredService: LayeredService,
    private productionService: ProductionService,
    private configService: ConfigService,
    private pageService: PageService,
  ) {}

  resolve() {
    const layeredData = this.getLayeredDataFromTransferState();

    if (layeredData.every((data) => data)) {
      return of({
        layeredFirst: layeredData[0],
        layeredSecond: layeredData[1],
        homePage: layeredData[2],
      });
    } else {
      return this.queryProducts();
    }
  }

  private queryProducts() {
    const homeCategoryEntity: HomeCategory = this.getHomeCategoryEntity();
    const homeCategoryEntity_2: HomeCategory = this.getHomeCategoryEntity_2();

    const layeredData$ = [
      this.fetchCategoryAndProducts(
        homeCategoryEntity,
        HOME_CATEGORY_ID_FOR_TRANSFER_STATE,
      ),
      this.fetchCategoryAndProducts(
        homeCategoryEntity_2,
        HOME_CATEGORY_ID_2_FOR_TRANSFER_STATE,
      ),
      this.pageService.getPage('home').pipe(
        shareReplay(1),
        tap((data) => {
          this.productionService.setDataToTransferState(
            HOME_PAGE_DATA_FOR_TRANSFER_STATE,
            data,
          );
        }),
      ),
    ];

    return combineLatest(layeredData$).pipe(
      map(([layeredFirst, layeredSecond, homePage]) => {
        return {
          layeredFirst: { title: homeCategoryEntity.title, ...layeredFirst },
          layeredSecond: { title: homeCategoryEntity_2.title, ...layeredSecond },
          homePage,
        };
      }),
    );
  }

  private getLayeredDataFromTransferState() {
    const layeredDataKeys = [
      HOME_CATEGORY_ID_FOR_TRANSFER_STATE,
      HOME_CATEGORY_ID_2_FOR_TRANSFER_STATE,
      HOME_PAGE_DATA_FOR_TRANSFER_STATE,
    ];
    const layeredData = layeredDataKeys.map((key) =>
      this.productionService.getDataFromTransferState(key),
    );
    return layeredData;
  }

  private fetchCategoryAndProducts(
    homeCategoryEntity: HomeCategory,
    categoryKey: string,
  ) {
    if (!homeCategoryEntity.id) {
      console.warn('no home category defined, or wrong category id');
      return EMPTY;
    }

    return this.categoryService.getCategoryById(Number(homeCategoryEntity.id)).pipe(
      switchMap((category) => {
        if (!category) {
          console.warn('no home category defined, or wrong category id');
          return of({} as Layered);
        }

        return this.getProductsByCategory(
          category,
          homeCategoryEntity.title,
          categoryKey,
        );
      }),
    );
  }

  private getProductsByCategory(
    category: Category,
    title: string,
    keyForTransferState: string,
  ) {
    this.layeredService.reset();
    this.layeredService.setCategory(category);
    this.layeredService.setPageSize(this.PAGE_SIZE);

    return this.layeredService.query().pipe(
      tap((layered) => {
        this.productionService.setDataToTransferState(keyForTransferState, {
          title,
          ...layered,
        });
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('could not fetch products for home category', error);
        return EMPTY;
      }),
    );
  }

  private getHomeCategoryEntity() {
    const homeCategoryId = 'speedy/general/home_category_id';
    const homeCategoryTitle = 'speedy/general/home_category_title';

    return {
      id: this.configService.getConfig(homeCategoryId),
      title: this.configService.getConfig(homeCategoryTitle),
    };
  }

  private getHomeCategoryEntity_2() {
    const homeCategoryId2 = 'speedy/general/home_category_id_2';
    const homeCategoryTitle2 = 'speedy/general/home_category_title_2';

    return {
      id: this.configService.getConfig(homeCategoryId2),
      title: this.configService.getConfig(homeCategoryTitle2),
    };
  }
}
